import React from "react";
import { useOutletContext } from "react-router-dom";
import { uniforms_getDeliveries, uniforms_recordDelivery } from "../../lib/firestoreAdapters";
import { USE_LOCAL_FALLBACK } from "../../config/firebaseConfig";

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (!lines.length) return [];
  const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
  return lines.slice(1).map(line => {
    const cols = line.split(",").map(c => c.trim());
    const obj = {};
    headers.forEach((h, i) => obj[h] = cols[i] || "");
    return obj;
  });
}

function deliveriesKey(campus) { return `uniforms_deliveries_${String(campus || "gen").toLowerCase()}`; }
function loadDeliveries(campus) { try { const raw = localStorage.getItem(deliveriesKey(campus)); return raw ? JSON.parse(raw) : []; } catch { return []; } }
function saveDeliveries(campus, data) { try { localStorage.setItem(deliveriesKey(campus), JSON.stringify(data)); } catch { void 0; } }
function inventoryKey(campus) { return `uniforms_inventory_${String(campus || "gen").toLowerCase()}`; }
function loadInventory(campus) { try { const raw = localStorage.getItem(inventoryKey(campus)); return raw ? JSON.parse(raw) : []; } catch { return []; } }
function saveInventory(campus, data) { try { localStorage.setItem(inventoryKey(campus), JSON.stringify(data)); } catch { void 0; } }

export default function UniformsDeliveries() {
  const { campus } = useOutletContext();
  const [list, setList] = React.useState([]);
  const [showNew, setShowNew] = React.useState(false);
  const [form, setForm] = React.useState({ supplier: "", drNo: "", date: new Date().toISOString().slice(0,10), itemsCSV: "" });
  const [saving, setSaving] = React.useState(false);
  const [info, setInfo] = React.useState("");

  React.useEffect(() => {
    (async () => {
      try {
        const rows = USE_LOCAL_FALLBACK ? loadDeliveries(campus) : await uniforms_getDeliveries(campus);
        setList(rows);
      } catch {
        setList([]);
      }
    })();
  }, [campus]);

  async function save() {
    const rows = parseCSV(form.itemsCSV);
    const items = rows.map(r => ({
      product: r.product || r.name || "",
      size: r.size || "",
      sku: r.sku || "",
      barcode: r.barcode || "",
      qty: Number(r.qty || r.quantity || 0),
      cost: Number(r.cost || 0),
      price: Number(r.price || 0),
    })).filter(it => it.product && (it.size || it.sku) && Number(it.qty || 0) > 0);
    if (!items.length) { setInfo("No items parsed. CSV needs headers: product,size,qty,cost,price[,sku,barcode]"); return; }
    setSaving(true); setInfo("");
    try {
      if (USE_LOCAL_FALLBACK) {
        const inv = loadInventory(campus);
        const nextInv = inv.map(row => ({ ...row }));
        for (const it of items) {
          const idx = nextInv.findIndex(r => String(r.product) === String(it.product) && String(r.size) === String(it.size));
          if (idx >= 0) {
            nextInv[idx] = {
              ...nextInv[idx],
              cost: Number(it.cost || nextInv[idx].cost || 0),
              price: Number(it.price || nextInv[idx].price || 0),
              stock: Number(nextInv[idx].stock || 0) + Number(it.qty || 0),
              sku: it.sku || nextInv[idx].sku || "",
              barcode: it.barcode || nextInv[idx].barcode || ""
            };
          } else {
            nextInv.push({
              product: it.product,
              size: it.size,
              sku: it.sku || "",
              barcode: it.barcode || "",
              cost: Number(it.cost || 0),
              price: Number(it.price || 0),
              stock: Number(it.qty || 0)
            });
          }
        }
        saveInventory(campus, nextInv);
        const rows = loadDeliveries(campus);
        rows.push({
          id: `D-${Date.now()}`,
          campus,
          supplier: form.supplier,
          drNo: form.drNo,
          date: form.date,
          items,
          totalCost: items.reduce((sum, it) => sum + Number(it.qty || 0) * Number(it.cost || 0), 0)
        });
        saveDeliveries(campus, rows);
        setList(rows);
      } else {
        await uniforms_recordDelivery(campus, { supplier: form.supplier, drNo: form.drNo, date: form.date, items });
        const rows = await uniforms_getDeliveries(campus);
        setList(rows);
      }
      setShowNew(false);
      setForm({ supplier: "", drNo: "", date: new Date().toISOString().slice(0,10), itemsCSV: "" });
    } catch (e) {
      setInfo("Network issue while saving. Please retry.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      {USE_LOCAL_FALLBACK && (
        <div className="alert alert-warning">Development mode: using local storage. In production this uses Firestore.</div>
      )}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Deliveries — {campus.toUpperCase()}</h5>
        <button className="btn btn-success" onClick={() => setShowNew(true)}>New Delivery</button>
      </div>
      {showNew && (
        <div className="card mb-3">
          <div className="card-header">New Delivery Receipt</div>
          <div className="card-body">
            <div className="row g-2 mb-2">
              <div className="col-md-4"><input className="form-control" placeholder="Supplier" value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} /></div>
              <div className="col-md-3"><input className="form-control" placeholder="DR No." value={form.drNo} onChange={e => setForm({ ...form, drNo: e.target.value })} /></div>
              <div className="col-md-3"><input type="date" className="form-control" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            </div>
            <div className="mb-2">
              <label className="form-label">Items CSV</label>
              <textarea className="form-control" rows={6} placeholder="product,size,qty,cost,price[,sku,barcode]" value={form.itemsCSV} onChange={e => setForm({ ...form, itemsCSV: e.target.value })} />
            </div>
            {info && <div className="text-danger mb-2">{info}</div>}
          </div>
          <div className="card-footer d-flex justify-content-end gap-2">
            <button className="btn btn-secondary" onClick={() => setShowNew(false)} disabled={saving}>Cancel</button>
            <button className="btn btn-success" onClick={save} disabled={saving}>Save Receipt</button>
          </div>
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th>Date</th><th>Supplier</th><th>DR No.</th><th className="text-end">Total Cost</th><th>Items</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan="5" className="text-center text-muted">No deliveries recorded.</td></tr>
            ) : list.map(r => (
              <tr key={r.id}>
                <td>{r.date}</td>
                <td>{r.supplier || "-"}</td>
                <td>{r.drNo || "-"}</td>
                <td className="text-end">₱{Number(r.totalCost || 0).toLocaleString()}</td>
                <td>{Array.isArray(r.items) ? r.items.map((it, idx) => <div key={idx}>{it.product} {it.size} × {it.qty} @ ₱{Number(it.cost || 0).toLocaleString()}</div>) : "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
